/**
 * OrderIssuesAlert — Dashboard: Aufträge, die gerade an einem automatischen
 * Schritt hängen oder dabei falsch behandelt wurden.
 *
 * Anlass (16.09.2026): Drei Bestellungen standen wochenlang fälschlich als
 * „versendet“ und wurden nicht gedruckt. Die Oberfläche hatte „Erfolg“
 * gemeldet, und fehlgeschlagene Schritte trugen Texte wie „Click failed“ ohne
 * Auftragsnummer. Tobias' Wunsch: Jede Meldung muss sagen, WAS los ist,
 * WELCHER Auftrag betroffen ist und WAS zu tun ist.
 *
 * Quellen (live):
 *  - shipping_queue, etsyCheck.status = wrong_order | missing_final
 *      Nachprüfung über die Etsy-Schnittstelle (scraper/etsy_verify.py)
 *  - shipping_queue, status = error      Als-versendet-Markieren gescheitert
 *  - stage_changes_queue, status = error Stufenwechsel gescheitert
 * Nicht mehr relevante Einträge werden ausgeblendet: Stufe inzwischen erreicht,
 * Bestellung nicht mehr in „Druck“, älter als 21 Tage, oder „Erledigt“ geklickt.
 * Die Kontroll-Fälle (falsche Bestellung) bleiben immer stehen, bis jemand sie
 * erledigt — die betroffene Bestellung ist dann ja gerade NICHT mehr aktiv.
 *
 * Die Meldungstexte kommen vollständig vom Scraper (errorMsg / etsyCheck.detail)
 * und nennen Auftrag, Grund und nächsten Schritt. Hier wird nur ergänzt, was
 * dort fehlen kann: Kundenname, Zeitpunkt, Anzahl der Versuche.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection, deleteField, doc, getDoc, onSnapshot, query,
  serverTimestamp, updateDoc, where,
} from "firebase/firestore";
import { db } from "../firebase";

const STAGE_LABELS = {
  new: "Neu", grafiker: "Grafiker", entwurf_raus: "Entwurf raus",
  produktion: "in Produktion", druck: "Druck", kerzen: "Kerzen",
};
const MAX_AGE_MS = 21 * 24 * 3600 * 1000;

const KIND = {
  wrong_order: {
    rank: 0, color: "#c53030", label: "Versand-Kontrolle: falsche Bestellung",
    hint: "Die Sendungsnummer hängt bei Etsy (auch) an einer anderen Kundin. " +
          "Deren Bestellung gilt als versendet und fällt aus der Produktion.",
  },
  missing_final: {
    rank: 1, color: "#c05621", label: "Versand-Kontrolle: bei Etsy nicht angekommen",
    hint: "Der Scraper hat „versendet“ gemeldet, Etsy zeigt die Sendung aber nicht.",
  },
  ship_error: {
    rank: 2, color: "#c05621", label: "Als versendet markieren fehlgeschlagen",
    hint: "Die Bestellung steht auf Etsy noch nicht als versendet.",
  },
  stage_error: {
    rank: 3, color: "#b7791f", label: "Stufenwechsel fehlgeschlagen",
    hint: "Die Bestellung steht auf Etsy noch in der alten Stufe.",
  },
};

const toMs = (v) => (v && typeof v.toMillis === "function" ? v.toMillis()
  : v instanceof Date ? v.getTime() : 0);
const fmt = (v) => {
  const ms = toMs(v);
  return ms ? new Date(ms).toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  }) : "–";
};
const etsyUrl = (id) =>
  `https://www.etsy.com/your/orders/sold?search_query=${id}&order_id=${id}`;

/**
 * Ein Warteschlangen-Eintrag -> Anzeigezeile oder null (nicht relevant).
 * Exportiert und getestet: diese Regeln entscheiden, was jemand sieht.
 */
export const toIssue = (source, row, order, nowMs = Date.now()) => {
  if (!row || row.issueDismissedAt) return null;
  const check = row.etsyCheck || {};
  const when = toMs(row.failedAt) || toMs(check.checkedAt) || toMs(row.createdAt);
  const orderId = String(row.orderId || "");
  const stage = order?.workflowStage;

  if (source === "shipping" && ["wrong_order", "missing_final"].includes(check.status)) {
    return {
      kind: check.status, orderId, when,
      text: check.detail ||
        `Versand-Kontrolle für #${orderId} meldet ${check.status}, ohne Beschreibung.`,
      otherOrderIds: check.otherOrderIds || [],
      tracking: row.trackingNumber, retryable: false,
    };
  }
  if (when && nowMs - when > MAX_AGE_MS) return null;
  if (source === "shipping" && row.status === "error") {
    if (stage && stage !== "druck") return null;      // inzwischen erledigt
    return {
      kind: "ship_error", orderId, when, tracking: row.trackingNumber,
      failCount: Number(row.failCount || 0), retryable: true,
      text: row.errorMsg ||
        `#${orderId} konnte nicht als versendet markiert werden ` +
        `(Sendung ${row.trackingNumber || "unbekannt"}). Ein Grund wurde nicht ` +
        `aufgezeichnet – bitte auf Etsy prüfen.`,
    };
  }
  if (source === "stage" && row.status === "error") {
    if (stage && (stage === row.targetStage || stage === "archived")) return null;
    const label = STAGE_LABELS[row.targetStage] || row.targetStage;
    return {
      kind: "stage_error", orderId, when, targetStage: row.targetStage,
      retryable: true,
      text: row.errorMsg && row.errorMsg !== "Click failed"
        ? row.errorMsg
        : `Stufenwechsel von #${orderId} auf „${label}“ ist fehlgeschlagen. ` +
          `Ein genauer Grund wurde damals nicht aufgezeichnet („Click failed“). ` +
          `Bitte die Stufe auf Etsy prüfen und bei Bedarf „Erneut versuchen“.`,
    };
  }
  return null;
};

const OrderIssuesAlert = () => {
  const [rows, setRows] = useState({ wrong: [], shipErr: [], stageErr: [] });
  const [orders, setOrders] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => {
    const listen = (q, key, source, coll) => onSnapshot(q, (snap) => {
      setRows((r) => ({ ...r, [key]: snap.docs.map((d) => ({
        id: d.id, source, coll, ...d.data(),
      })) }));
    }, (err) => console.error("OrderIssuesAlert:", key, err));
    const u1 = listen(query(collection(db, "shipping_queue"),
      where("etsyCheck.status", "in", ["wrong_order", "missing_final"])),
      "wrong", "shipping", "shipping_queue");
    const u2 = listen(query(collection(db, "shipping_queue"),
      where("status", "==", "error")), "shipErr", "shipping", "shipping_queue");
    const u3 = listen(query(collection(db, "stage_changes_queue"),
      where("status", "==", "error")), "stageErr", "stage", "stage_changes_queue");
    return () => { u1(); u2(); u3(); };
  }, []);

  // Bestellungen nachladen (Name, aktuelle Stufe) — nur die betroffenen.
  const allRows = useMemo(
    () => [...rows.wrong, ...rows.shipErr, ...rows.stageErr], [rows]);
  useEffect(() => {
    const ids = new Set();
    for (const r of allRows) {
      if (r.orderId) ids.add(String(r.orderId));
      for (const o of r.etsyCheck?.otherOrderIds || []) ids.add(String(o));
    }
    const missing = [...ids].filter((id) => !(id in orders));
    if (!missing.length) return;
    let cancelled = false;
    Promise.all(missing.map(async (id) => {
      try {
        const s = await getDoc(doc(db, "etsy_orders", id));
        return [id, s.exists() ? s.data() : null];
      } catch { return [id, null]; }
    })).then((pairs) => {
      if (!cancelled) setOrders((o) => ({ ...o, ...Object.fromEntries(pairs) }));
    });
    return () => { cancelled = true; };
  }, [allRows, orders]);

  const issues = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of allRows) {
      const key = `${r.coll}/${r.id}`;
      if (seen.has(key)) continue;
      const issue = toIssue(r.source, r, orders[String(r.orderId)]);
      if (issue) {
        seen.add(key);
        out.push({ ...issue, key, coll: r.coll, docId: r.id });
      }
    }
    return out.sort((a, b) =>
      KIND[a.kind].rank - KIND[b.kind].rank || b.when - a.when);
  }, [allRows, orders]);

  if (issues.length === 0) return null;

  const act = async (issue, patch) => {
    setBusy((b) => ({ ...b, [issue.key]: true }));
    try {
      await updateDoc(doc(db, issue.coll, issue.docId), patch);
    } catch (e) {
      alert(`Aktion für #${issue.orderId} fehlgeschlagen: ${e.message}`);
    } finally {
      setBusy((b) => ({ ...b, [issue.key]: false }));
    }
  };
  const retry = (issue) => act(issue, {
    status: "pending", failCount: 0, errorMsg: deleteField(),
    retryRequestedAt: serverTimestamp(),
  });
  const dismiss = (issue) => act(issue, { issueDismissedAt: serverTimestamp() });

  const btn = {
    fontSize: "0.76rem", padding: "0.25em 0.7em", borderRadius: 6,
    border: "1px solid #cbd5e0", background: "#fff", color: "#2d3748",
    cursor: "pointer", whiteSpace: "nowrap", textDecoration: "none",
  };

  return (
    <div style={{
      background: "#fff5f5", border: "1px solid #feb2b2", borderRadius: 12,
      padding: "1.1em 1.4em", marginBottom: "1.5em",
    }}>
      <div style={{ fontSize: "0.85rem", color: "#c53030", fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.03em",
                    marginBottom: "0.7em" }}>
        ⚠ Aufträge mit Handlungsbedarf ({issues.length})
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.6em" }}>
        {issues.map((it) => {
          const k = KIND[it.kind];
          const order = orders[it.orderId];
          return (
            <div key={it.key} style={{
              background: "#fff", borderRadius: 8, padding: "0.7em 1em",
              border: `1px solid ${it.kind === "wrong_order" ? "#fc8181" : "#fed7d7"}`,
              borderLeft: `4px solid ${k.color}`, fontSize: "0.84rem",
              opacity: busy[it.key] ? 0.55 : 1,
            }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5em",
                            alignItems: "baseline" }}>
                <span style={{ fontWeight: 700, color: k.color }}>{k.label}</span>
                <span style={{ fontWeight: 700, color: "#2d3748" }}>#{it.orderId}</span>
                <span style={{ color: "#4a5568" }}>
                  {order?.buyerName || "Kundin unbekannt"}
                  {order?.workflowStage && ` · Stufe: ${STAGE_LABELS[order.workflowStage] || order.workflowStage}`}
                  {it.targetStage && ` · Ziel: ${STAGE_LABELS[it.targetStage] || it.targetStage}`}
                  {it.tracking && ` · Sendung ${it.tracking}`}
                </span>
                <span style={{ color: "#a0aec0", marginLeft: "auto" }}
                      title="Zeitpunkt der letzten Meldung">
                  {fmt(it.when)}
                  {it.failCount > 0 && ` · ${it.failCount}. Fehlversuch`}
                </span>
              </div>
              <div style={{ color: "#742a2a", marginTop: "0.3em", lineHeight: 1.45 }}>
                {it.text}
              </div>
              {it.failCount >= 3 && (
                <div style={{ color: "#975a16", fontSize: "0.78rem", marginTop: "0.2em" }}>
                  Automatische Wiederholung nach 3 Fehlversuchen gestoppt.
                </div>
              )}
              <div style={{ color: "#a0aec0", fontSize: "0.74rem", marginTop: "0.2em" }}>
                {k.hint}
              </div>
              <div style={{ display: "flex", gap: "0.5em", marginTop: "0.5em",
                            flexWrap: "wrap" }}>
                <a href={etsyUrl(it.orderId)} target="_blank" rel="noopener noreferrer"
                   style={btn}>Etsy #{it.orderId} →</a>
                {(it.otherOrderIds || []).map((o) => (
                  <a key={o} href={etsyUrl(o)} target="_blank" rel="noopener noreferrer"
                     style={{ ...btn, borderColor: "#fc8181", color: "#c53030" }}>
                    Etsy #{o} ({orders[o]?.buyerName || "andere Kundin"}) →
                  </a>
                ))}
                {it.retryable && (
                  <button type="button" style={btn} disabled={busy[it.key]}
                          onClick={() => retry(it)}
                          title="Setzt den Auftrag wieder in die Warteschlange; der Scraper versucht es beim nächsten Durchlauf erneut.">
                    ↺ Erneut versuchen
                  </button>
                )}
                <button type="button" style={btn} disabled={busy[it.key]}
                        onClick={() => dismiss(it)}
                        title="Blendet die Meldung aus. Der Eintrag und sein Grund bleiben gespeichert.">
                  ✓ Erledigt
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderIssuesAlert;
